/**
 * Map a saved view's stored filters onto a BillFilters state — shared by the
 * Legislation page (useSavedViewState) and the workspace Add Bills modal so
 * the two cannot drift on how a view is read.
 *
 * Stored filter JSON is an unchecked cast: rows saved before multi-select hold
 * strings, so array fields are coerced via toFilterArray. Keys absent from the
 * saved row fall back to `base`.
 */

import type { SavedView } from '../types/savedViews';
import type { BillFilters } from '../hooks/useBillFilters';
import { toFilterArray } from '../hooks/useBillFilters';
import { DEFAULT_SEARCH_SCOPE, type LegislationSearchScope } from './legislationUrlState';

export interface AppliedSavedViewFilters {
  filters: BillFilters;
  searchQuery: string;
  searchScope: LegislationSearchScope;
}

export function savedViewToBillFilters(base: BillFilters, view: SavedView): AppliedSavedViewFilters {
  const savedCopy = { ...(view.filters as Record<string, unknown>) };
  const savedScope = savedCopy.legislationSearchScope;
  delete savedCopy.legislationSearchScope;

  const searchScope: LegislationSearchScope =
    savedScope === 'fulltext' || savedScope === 'metadata'
      ? (savedScope as LegislationSearchScope)
      : DEFAULT_SEARCH_SCOPE;

  const filters: BillFilters = {
    ...base,
    ...(savedCopy as Partial<BillFilters>),
    includeIntroduced: view.filters.includeIntroduced || false,
    includeAmended: view.filters.includeAmended || false,
    startDate: view.filters.startDate || '',
    endDate: view.filters.endDate || '',
    trackingStatus: view.filters.trackingStatus || '',
    subject: toFilterArray('subject' in savedCopy ? savedCopy.subject : base.subject, ['All subjects']),
    status: toFilterArray('status' in savedCopy ? savedCopy.status : base.status),
    sponsor: toFilterArray('sponsor' in savedCopy ? savedCopy.sponsor : base.sponsor),
    committee_id: toFilterArray('committee_id' in savedCopy ? savedCopy.committee_id : base.committee_id),
    session: toFilterArray('session' in savedCopy ? savedCopy.session : base.session),
  };

  return { filters, searchQuery: view.searchQuery || '', searchScope };
}
